import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface MyAssociations {
  asMember: Array<{
    membershipId: string;
    role: string;
    committeeRole: string;
    association: { id: string; name: string; slug: string; logo: string | null; coverImage: string | null; city: string | null; status: string; themeColor: string | null };
  }>;
  asManager: Array<{
    assignmentId: string;
    scope: string;
    association: { id: string; name: string; slug: string; logo: string | null; coverImage: string | null; city: string | null; status: string; themeColor: string | null };
  }>;
}

export function MyAssociationsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'associations'],
    queryFn: () => api.get<ApiSuccess<MyAssociations>>('/api/associations/me'),
  });

  if (isLoading) return <div className="text-gray-500">⟳ جاري التحميل…</div>;
  const d = data?.data;
  const memberCount = d?.asMember.length ?? 0;
  const mgrCount = d?.asManager.length ?? 0;

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-2">🏛️ جمعياتي</h1>
      <p className="text-gray-600 mb-6">الجمعيات التي تنتمي إليها أو تديرها</p>

      {memberCount + mgrCount === 0 ? (
        <Card><CardBody>
          <div className="text-center py-12 text-gray-500">
            <div className="text-5xl mb-3">🏛️</div>
            <div>لست عضواً في أي جمعية بعد</div>
            <p className="text-xs mt-2">إذا تلقيت دعوة، انضم عبر الرابط المُرسل لك</p>
          </div>
        </CardBody></Card>
      ) : (
        <>
          {mgrCount > 0 && (
            <>
              <h2 className="text-xl font-black text-navy-dark mb-3">👨‍💼 الجمعيات التي تديرها</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {d!.asManager.map((a) => <AssociationCard key={a.association.id} a={a.association} subtitle={`نطاق: ${a.scope}`} />)}
              </div>
            </>
          )}
          {memberCount > 0 && (
            <>
              <h2 className="text-xl font-black text-navy-dark mb-3">👥 عضوياتك</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {d!.asMember.map((m) => <AssociationCard key={m.association.id} a={m.association} subtitle={m.committeeRole !== 'NONE' ? m.committeeRole : m.role} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function AssociationCard({ a, subtitle }: { a: MyAssociations['asMember'][0]['association']; subtitle: string }) {
  return (
    <Link to={`/associations/${a.slug}`}>
      <Card className="hover:shadow-lg transition cursor-pointer overflow-hidden">
        <div className="h-32 relative" style={{ background: a.themeColor ?? '#1e3a5f' }}>
          {a.coverImage && <img src={a.coverImage} alt="" className="w-full h-full object-cover" />}
          {a.logo && <img src={a.logo} alt="" className="absolute -bottom-6 right-4 w-14 h-14 rounded-full border-4 border-white object-cover" />}
        </div>
        <CardBody>
          <h3 className="font-black text-navy-dark text-lg">{a.name}</h3>
          <p className="text-xs text-gray-500 mb-2">{a.city ?? '—'}</p>
          <div className="flex justify-between items-center">
            <Badge color="navy">{subtitle}</Badge>
            <Badge color={a.status === 'ACTIVE' ? 'green' : 'amber'}>{a.status}</Badge>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}
